import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, RefreshCw, Flag, ExternalLink, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ReanalyzeFieldsPopover from '@/components/ReanalyzeFieldsPopover';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  listPendingStories,
  listOpenStoryReports,
  resolveAllOpenReportsForStory,
} from '@/services/storyRepo';
import {
  moderateStory,
  backfillEmbeddings,
  reanalyzeAllStories,
  REANALYZE_BATCH_SIZE,
} from '@/services/storyService';

const shortId = (uuid) => (uuid ? `${uuid.slice(0, 8)}…` : 'anonymous');

const ACTION_TO_STATUS = { approve: 'approved', reject: 'rejected' };

const ModerationDashboard = () => {
  const navigate = useNavigate();
  const { user, isModerator, loading: authLoading } = useAuth();
  const [stories, setStories] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reportsError, setReportsError] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [action, setAction] = useState('');
  const [reason, setReason] = useState('');
  const [backfilling, setBackfilling] = useState(false);
  // Re-analysis runs LLM extraction batch-by-batch across the corpus.
  // We track running state + live progress (updated/failed/processed)
  // so the button label communicates what's happening, since each
  // batch can take 30-60s.
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reanalyzeProgress, setReanalyzeProgress] = useState(null);
  /** Remove-from-public: modal target + moderator explanation (stored as `moderator_notes`). */
  const [removePublicTarget, setRemovePublicTarget] = useState(null);
  const [removePublicNotes, setRemovePublicNotes] = useState('');

  const fetchPending = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const fetched = await listPendingStories();
      setStories(fetched);
    } catch (error) {
      console.error('Error fetching pending stories:', error);
      const message = error?.message || 'Failed to load pending stories';
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    setReportsError(null);
    try {
      const rows = await listOpenStoryReports();
      setReports(rows);
    } catch (error) {
      console.error('Error fetching story reports:', error);
      const message = error?.message || 'Failed to load reports';
      setReportsError(message);
      toast.error(message);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  // Depend on user?.id (primitive) instead of `user` (fresh object reference
  // on every AuthContext re-render) to avoid spurious refetch loops.
  const userId = user?.id;
  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      toast.error('Please login to access moderation');
      navigate('/login');
      return;
    }
    if (!isModerator) {
      toast.error('Moderator access required');
      navigate('/');
      return;
    }
    fetchPending();
    fetchReports();
  }, [userId, isModerator, authLoading, navigate, fetchPending, fetchReports]);

  const handleModeration = async () => {
    if (!selectedStory) return;
    const status = ACTION_TO_STATUS[action];
    if (!status) return;
    if (action === 'reject') {
      const r = (reason || '').trim();
      if (r.length < 10) {
        toast.error('Please write at least 10 characters so the author knows why the story was rejected.');
        return;
      }
    }
    try {
      await moderateStory(selectedStory.id, status, action === 'reject' ? reason.trim() : reason || '');
      toast.success(`Story ${status}`);
      setShowDialog(false);
      setSelectedStory(null);
      setReason('');
      fetchPending();
      fetchReports();
    } catch (error) {
      console.error('Error moderating story:', error);
      toast.error(error.message || 'Failed to moderate story');
    }
  };

  const handleBackfill = async () => {
    if (backfilling) return;
    setBackfilling(true);
    try {
      const result = await backfillEmbeddings();
      const updated = result?.updated ?? 0;
      const failed = result?.failed ?? 0;
      toast.success(`Backfill done — embedded ${updated}, failed ${failed}`);
    } catch (err) {
      console.error('Backfill failed:', err);
      toast.error(err?.message || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  // Loops km_reanalyze_all_stories in batches until the server reports
  // no more rows. ONLY the listed `fields` are overwritten — every other
  // column (including manually-curated practice_ids, life_areas, etc.)
  // stays exactly as it was. This is intentional: operators routinely
  // augment LLM tags by hand and a default "overwrite everything" would
  // erase that work.
  //
  // Each fieldOption in REANALYZE_FIELDS is one operator-friendly
  // surface for "I changed the prompt for X, refresh X across the
  // corpus." Add more entries here as needed.
  const handleReanalyze = async ({ fields, label, rebuildChunks = false }) => {
    if (reanalyzing) return;
    const ok = window.confirm(
      `Re-run Gemini extraction and overwrite ${label} on every approved story?\n\n`
      + `Fields that will be overwritten: ${fields.join(', ')}.\n\n`
      + 'All other columns (including manually-added tags or practices) '
      + 'are preserved. Each story is also re-embedded with the merged '
      + 'state.\n\n'
      + (rebuildChunks
        ? 'Also REBUILDS the semantic-chunk lane (km_story_chunks) for each '
          + 'story — extra embedding writes per story.\n\n'
        : '')
      + 'Cost: ~1 Gemini call per story. Cannot be undone.'
    );
    if (!ok) return;

    setReanalyzing(true);
    setReanalyzeProgress({ updated: 0, failed: 0, processed: 0, label });
    let totalUpdated = 0;
    let totalFailed = 0;
    let totalProcessed = 0;
    let cursor = null;
    try {
      // Bounded loop — defense against a server bug that always returns
      // a cursor. Stops after iterating much more than any plausible
      // corpus size.
      for (let i = 0; i < 200; i += 1) {
        const result = await reanalyzeAllStories({
          fields,
          limit: REANALYZE_BATCH_SIZE,
          beforeCreatedAt: cursor,
          rebuildChunks,
        });
        const processed = result?.processed ?? 0;
        totalUpdated  += result?.updated ?? 0;
        totalFailed   += result?.failed  ?? 0;
        totalProcessed += processed;
        setReanalyzeProgress({
          updated: totalUpdated,
          failed: totalFailed,
          processed: totalProcessed,
          label,
        });
        if (!result?.next_cursor || processed === 0) break;
        cursor = result.next_cursor;
      }
      toast.success(
        `${label} reanalysis done — updated ${totalUpdated}, failed ${totalFailed} `
        + `(scanned ${totalProcessed})`
      );
    } catch (err) {
      console.error('Reanalyze failed:', err);
      toast.error(err?.message || 'Reanalyze failed');
    } finally {
      setReanalyzing(false);
      setReanalyzeProgress(null);
    }
  };


  const openDialog = (story, moderationAction) => {
    setSelectedStory(story);
    setAction(moderationAction);
    setShowDialog(true);
  };

  const reportsByStory = useMemo(() => {
    const map = new Map();
    for (const r of reports) {
      if (!map.has(r.story_id)) {
        map.set(r.story_id, {
          story_id: r.story_id,
          story_title: r.story_title,
          story_status: r.story_status,
          story_author_name: r.story_author_name,
          reports: [],
        });
      }
      map.get(r.story_id).reports.push(r);
    }
    const groups = Array.from(map.values());
    // Triage: most-reported stories first; tie-break by newest report in the group.
    groups.sort((a, b) => {
      const n = b.reports.length - a.reports.length;
      if (n !== 0) return n;
      const latestMs = (g) =>
        Math.max(...g.reports.map((x) => new Date(x.created_at).getTime()));
      return latestMs(b) - latestMs(a);
    });
    return groups;
  }, [reports]);

  const handleResolveAllForStory = async (storyId, status, successMessage) => {
    try {
      await resolveAllOpenReportsForStory(storyId, status);
      toast.success(successMessage);
      fetchReports();
    } catch (error) {
      console.error('Error resolving reports for story:', error);
      toast.error(error?.message || 'Could not update reports');
    }
  };

  const openRemovePublicDialog = (storyId, storyTitle, openReportCount) => {
    setRemovePublicTarget({ storyId, storyTitle, openReportCount });
    setRemovePublicNotes('');
  };

  const handleConfirmRemoveFromPublic = async () => {
    const t = removePublicTarget;
    if (!t) return;
    const notes = removePublicNotes.trim();
    if (notes.length < 10) {
      toast.error('Please write at least 10 characters so the author knows why the story was removed.');
      return;
    }
    try {
      await moderateStory(t.storyId, 'rejected', notes);
      await resolveAllOpenReportsForStory(t.storyId, 'reviewed');
      toast.success('Story removed from public and reports closed');
      setRemovePublicTarget(null);
      setRemovePublicNotes('');
      fetchReports();
      fetchPending();
    } catch (error) {
      console.error('Error removing story from public:', error);
      toast.error(error?.message || 'Could not remove story');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <PageHeader>
        <ToolbarCaptionButton
          label="Back to Home"
          shortLabel="Home"
          onClick={() => navigate('/')}
          data-testid="back-home-btn"
        />
      </PageHeader>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight leading-tight text-[#2D2A26] mb-4">
              Moderation Dashboard
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#6B665E]">
              Triage reader reports and review stories awaiting approval
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <Button
              onClick={handleBackfill}
              disabled={backfilling || reanalyzing}
              variant="outline"
              className="border-[#C05621] text-[#C05621] hover:bg-[#C05621] hover:text-white shrink-0"
              data-testid="backfill-embeddings-btn"
              title="Re-embed any stories whose embedding is missing"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${backfilling ? 'animate-spin' : ''}`} />
              {backfilling ? 'Embedding…' : 'Backfill embeddings'}
            </Button>
            <ReanalyzeFieldsPopover
              onApply={handleReanalyze}
              running={reanalyzing}
              disabled={backfilling}
              runningLabel={reanalyzing
                ? `${reanalyzeProgress?.label ?? 'Reanalyzing'}… ${reanalyzeProgress?.updated ?? 0}/${reanalyzeProgress?.processed ?? 0}`
                : null}
              triggerTestId="reanalyze-stories-btn"
              buttonClassName="border-[#C05621] text-[#C05621] hover:bg-[#C05621] hover:text-white shrink-0"
              align="end"
            />
          </div>
        </div>

        <Tabs defaultValue="reports" className="w-full">
          <TabsList className="mb-6 bg-[#F0EBE3] border border-[#E6E2D6]">
            <TabsTrigger value="reports" className="data-[state=active]:bg-white data-[state=active]:text-[#2D2A26]">
              Open reports
              {reports.length > 0 && (
                <span className="ml-2 rounded-full bg-[#C05621] text-white text-xs px-2 py-0.5">
                  {reports.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-white data-[state=active]:text-[#2D2A26]">
              Pending approval
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reports">
            {reportsLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C05621]" />
                <p className="mt-4 text-[#6B665E]">Loading reports...</p>
              </div>
            ) : reportsError ? (
              <div className="text-center py-12 bg-white rounded-xl border border-red-200">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-xl text-[#2D2A26] font-serif">Couldn't load reports</p>
                <p className="text-sm text-[#6B665E] mt-2 max-w-md mx-auto break-words">{reportsError}</p>
                <Button
                  onClick={fetchReports}
                  className="mt-6 bg-[#C05621] hover:bg-[#A04521] text-white"
                  data-testid="retry-reports-btn"
                >
                  Retry
                </Button>
              </div>
            ) : reportsByStory.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="open-reports-list">
                {reportsByStory.map((group) => {
                  const isApproved = group.story_status === 'approved';
                  return (
                    <div
                      key={group.story_id}
                      className="bg-white rounded-xl border border-[#E6E2D6] p-6"
                      data-testid={`story-report-group-${group.story_id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2 text-[#6B665E]">
                          <Flag className="h-4 w-4 shrink-0" />
                          <span className="text-xs uppercase tracking-wide">
                            {group.reports.length} {group.reports.length === 1 ? 'report' : 'reports'}
                          </span>
                        </div>
                        <span className="text-xs text-[#6B665E] whitespace-nowrap">
                          Latest: {new Date(group.reports[0].created_at).toLocaleString()}
                        </span>
                      </div>
                      <h3 className="text-xl font-serif font-medium text-[#2D2A26] mb-1 break-words">
                        {group.story_title}
                      </h3>
                      <p className="text-xs text-[#6B665E] mb-4">
                        Story status: <span className="font-medium">{group.story_status || '—'}</span>
                        {' · '}Author label: {group.story_author_name}
                      </p>

                      <div className="space-y-3 mb-4">
                        {group.reports.map((rep) => (
                          <div
                            key={rep.id}
                            className="rounded-md border border-[#E6E2D6] bg-[#FBF9F4] p-3"
                            data-testid={`story-report-${rep.id}`}
                          >
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-xs text-[#6B665E]">
                                Reporter: <code className="font-mono">{shortId(rep.reporter_id)}</code>
                              </span>
                              <span className="text-xs text-[#6B665E] whitespace-nowrap">
                                {new Date(rep.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-[#2D2A26]">
                              <span className="font-medium">Reason:</span> {rep.reason}
                            </p>
                            {rep.details ? (
                              <p className="text-sm text-[#6B665E] mt-1 whitespace-pre-wrap">
                                {rep.details}
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/story/${group.story_id}`)}
                          className="border-[#E6E2D6]"
                          data-testid={`report-open-story-${group.story_id}`}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Open story
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleResolveAllForStory(
                              group.story_id,
                              'dismissed',
                              'Reports dismissed'
                            )
                          }
                          className="border-[#E6E2D6] text-[#6B665E]"
                          data-testid={`report-dismiss-${group.story_id}`}
                        >
                          Dismiss
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="bg-[#4A5D4F] hover:bg-[#3A4D3F] text-white"
                          onClick={() =>
                            handleResolveAllForStory(
                              group.story_id,
                              'reviewed',
                              'Reports marked reviewed'
                            )
                          }
                          data-testid={`report-reviewed-${group.story_id}`}
                        >
                          Mark reviewed
                        </Button>
                        {isApproved && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() =>
                              openRemovePublicDialog(
                                group.story_id,
                                group.story_title,
                                group.reports.length
                              )
                            }
                            data-testid={`report-remove-${group.story_id}`}
                          >
                            <EyeOff className="h-4 w-4 mr-1" />
                            Remove from public
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-[#E6E2D6]">
                <CheckCircle className="h-12 w-12 text-[#4A5D4F] mx-auto mb-4" />
                <p className="text-xl text-[#2D2A26] font-serif">No open reports</p>
                <p className="text-sm text-[#6B665E] mt-2">Reader flags will appear here.</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="pending">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C05621]" />
                <p className="mt-4 text-[#6B665E]">Loading stories...</p>
              </div>
            ) : loadError ? (
              <div className="text-center py-12 bg-white rounded-xl border border-red-200">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-xl text-[#2D2A26] font-serif">Couldn't load stories</p>
                <p className="text-sm text-[#6B665E] mt-2 max-w-md mx-auto break-words">{loadError}</p>
                <Button
                  onClick={fetchPending}
                  className="mt-6 bg-[#C05621] hover:bg-[#A04521] text-white"
                  data-testid="retry-load-btn"
                >
                  Retry
                </Button>
              </div>
            ) : stories.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="pending-stories-list">
                {stories.map((story) => (
                  <div
                    key={story.id}
                    className="bg-white rounded-xl border border-[#E6E2D6] p-6"
                    data-testid={`pending-story-${story.id}`}
                  >
                    <div className="mb-4">
                      <span className="text-sm font-medium tracking-wide uppercase text-[#6B665E]">
                        {story.category}
                      </span>
                      <h3 className="text-2xl font-serif font-medium text-[#2D2A26] mt-2 mb-2">
                        {story.title}
                      </h3>
                      <p className="text-sm text-[#6B665E]">by {story.author_name}</p>
                      <p className="text-xs text-[#6B665E] mt-1">
                        Submitted: {new Date(story.created_at).toLocaleDateString()}
                      </p>
                    </div>

                    <p className="text-base leading-relaxed text-[#6B665E] mb-6 line-clamp-3">
                      {story.preview}
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Button
                        onClick={() => navigate(`/story/${story.id}`)}
                        variant="outline"
                        className="w-full sm:min-w-0"
                        data-testid={`view-story-${story.id}`}
                      >
                        View Full Story
                      </Button>
                      <Button
                        onClick={() => openDialog(story, 'approve')}
                        className="w-full bg-[#4A5D4F] hover:bg-[#3A4D3F] text-white"
                        data-testid={`approve-btn-${story.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => openDialog(story, 'reject')}
                        variant="destructive"
                        className="w-full"
                        data-testid={`reject-btn-${story.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-[#E6E2D6]">
                <CheckCircle className="h-12 w-12 text-[#4A5D4F] mx-auto mb-4" />
                <p className="text-xl text-[#2D2A26] font-serif">No pending stories</p>
                <p className="text-sm text-[#6B665E] mt-2">All stories have been reviewed</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog
        open={!!removePublicTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRemovePublicTarget(null);
            setRemovePublicNotes('');
          }
        }}
      >
        <DialogContent data-testid="remove-public-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-[#2D2A26]">
              Remove from public
            </DialogTitle>
          </DialogHeader>
          {removePublicTarget ? (
            <div className="space-y-4">
              <p className="text-sm text-[#6B665E]">
                <span className="font-medium text-[#2D2A26]">“{removePublicTarget.storyTitle}”</span>
                {' '}will be set to <strong>rejected</strong> (no longer visible to readers).{' '}
                {removePublicTarget.openReportCount > 0 ? (
                  <>
                    The author will see how many lifetime reader reports this story received and the
                    note below (not individual reporter identities).
                  </>
                ) : (
                  <>The author will see the note below.</>
                )}
              </p>
              {removePublicTarget.openReportCount > 0 ? (
                <p className="text-xs text-[#6B665E]">
                  Open reports on this card:{' '}
                  <strong>{removePublicTarget.openReportCount}</strong> (will be marked reviewed).
                </p>
              ) : null}
              <div>
                <label
                  htmlFor="remove-public-notes"
                  className="text-sm font-medium text-[#2D2A26]"
                >
                  Why are you removing it? (visible to the author)
                </label>
                <Textarea
                  id="remove-public-notes"
                  value={removePublicNotes}
                  onChange={(e) => setRemovePublicNotes(e.target.value)}
                  placeholder="Brief, respectful explanation — e.g. policy concern and what they can change."
                  className="mt-2 min-h-32"
                  data-testid="remove-public-notes-input"
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRemovePublicTarget(null);
                setRemovePublicNotes('');
              }}
              data-testid="remove-public-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmRemoveFromPublic}
              data-testid="remove-public-confirm-btn"
            >
              Remove from public
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="moderation-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-[#2D2A26]">
              {action === 'approve' ? 'Approve Story' : 'Reject Story'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-[#6B665E]">
              {action === 'approve'
                ? 'Are you sure you want to approve this story? It will be visible to all users.'
                : 'Please provide a reason for rejecting this story:'}
            </p>

            {action === 'reject' && (
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for rejection..."
                className="min-h-32"
                data-testid="rejection-reason-input"
              />
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDialog(false);
                setReason('');
              }}
              data-testid="cancel-moderation-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleModeration}
              className={action === 'approve' ? 'bg-[#4A5D4F] hover:bg-[#3A4D3F]' : ''}
              variant={action === 'reject' ? 'destructive' : 'default'}
              data-testid="confirm-moderation-btn"
            >
              {action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModerationDashboard;
